import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Loader2, Plus, Scale } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { DatePicker } from "@/components/common/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const CREDIT_KINDS = { immobilier_fixe: "Prêt immobilier taux fixe", immobilier_variable: "Prêt immobilier taux variable", pret_relais: "Prêt relais",
  consommation: "Crédit à la consommation", renouvelable: "Crédit renouvelable", travaux: "Prêt travaux", decouvert: "Découvert autorisé" };
export const USURY_STATUS = { ok: ["Conforme", "bg-sage/20 text-foreground"], near: ["Proche du seuil", "bg-butter/50 text-foreground"], over: ["Au-dessus du seuil", "bg-destructive/15 text-destructive"] };
const pct = (v) => `${Number(v).toFixed(2).replace(".", ",")} %`;
const iso = (d) => d.toISOString().slice(0, 10);

const CreditForm = ({ onDone }) => {
  const [v, setV] = useState({ kind: "consommation", label: "", lender: "", amount: "", duration_months: "", taeg: "", account_id: "", account_name: "" });
  const { data: accounts = [] } = useQuery({ queryKey: ["bank-accounts"], queryFn: () => api.get("/finance/accounts").then((r) => r.data) });
  const mine = accounts.filter((a) => a.is_mine);
  const od = v.kind === "decouvert";
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/credits", od ? { kind: "decouvert", amount: Number(v.amount), ...(mine.length ? { account_id: Number(v.account_id) } : { account_name: v.account_name.trim() }) }
        : { ...v, account_id: null, account_name: null, lender: v.lender || null, amount: Number(v.amount), duration_months: Number(v.duration_months), taeg: Number(v.taeg) });
      toast.success(od ? "Découvert autorisé enregistré" : "Crédit ajouté");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-xl border border-dashed border-border p-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="credit-form">
      <Select value={v.kind} onValueChange={set("kind")}><SelectTrigger data-testid="credit-input-kind"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(CREDIT_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      {od ? <>
        {mine.length ? <Select value={v.account_id} onValueChange={set("account_id")}><SelectTrigger data-testid="credit-input-account"><SelectValue placeholder="Sur quel compte ?" /></SelectTrigger>
          <SelectContent>{mine.map((a) => <SelectItem key={a.bridge_id} value={String(a.bridge_id)}>{a.name}</SelectItem>)}</SelectContent></Select>
          : <Input value={v.account_name} onChange={set("account_name")} placeholder="Compte (ex. Compte courant Crédit Agricole)" required minLength={2} maxLength={60} data-testid="credit-input-account-name" />}
        <Input type="number" min="1" step="0.01" value={v.amount} onChange={set("amount")} placeholder="Montant autorisé €" required data-testid="credit-input-amount" />
        <Button type="submit" disabled={mine.length > 0 && !v.account_id} className="rounded-full sm:col-span-2 lg:col-span-3" data-testid="credit-submit">Enregistrer le découvert autorisé</Button>
      </> : <>
      <Input value={v.label} onChange={set("label")} placeholder="Nom du crédit" required minLength={2} data-testid="credit-input-label" />
      <Input value={v.lender} onChange={set("lender")} placeholder="Organisme prêteur" data-testid="credit-input-lender" />
      <Input type="number" min="1" step="0.01" value={v.amount} onChange={set("amount")} placeholder="Montant €" required data-testid="credit-input-amount" />
      <Input type="number" min="1" max="480" step="1" value={v.duration_months} onChange={set("duration_months")} placeholder="Durée (mois)" required data-testid="credit-input-duration" />
      <Input type="number" min="0" max="100" step="0.01" value={v.taeg} onChange={set("taeg")} placeholder="TAEG %" required data-testid="credit-input-taeg" />
      <Button type="submit" className="rounded-full sm:col-span-2 lg:col-span-3" data-testid="credit-submit">Ajouter le crédit</Button>
      </>}
    </form>
  );
};

const Credits = () => {
  const qc = useQueryClient();
  const { money } = useFormat();
  const [adding, setAdding] = useState(false);
  const { data = [] } = useQuery({ queryKey: ["credits"], queryFn: () => api.get("/finance/credits").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["credits"] });
  const del = async (id) => { try { await api.delete(`/finance/credits/${id}`); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold">Mes crédits</h3>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAdding(!adding)} data-testid="credit-add-button"><Plus className="mr-1 h-3.5 w-3.5" /> Ajouter</Button></div>
      {adding && <CreditForm onDone={() => { setAdding(false); refresh(); }} />}
      {data.length ? (
        <ul className="grid gap-2 md:grid-cols-2" data-testid="credits-list">
          {data.map((c) => (
            <li key={c.id} className="rounded-xl border border-border bg-card/70 p-3 text-sm" data-testid={`credit-${c.id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1"><div className="truncate font-semibold">{c.label}</div>
                  <div className="text-xs text-muted-foreground">{c.kind_label} · {money(c.amount, { maximumFractionDigits: 0 })}{c.duration_months ? ` · ${c.duration_months} mois` : ""}{c.lender ? ` · ${c.lender}` : ""}</div></div>
                <ConfirmDelete title="Supprimer ce crédit ?" text={`« ${c.label} » sera retiré.`} testid={`credit-delete-${c.id}`} onConfirm={() => del(c.id)} />
              </div>
              {c.usury && <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span>TAEG <b className="num">{pct(c.taeg)}</b> · seuil <span className="num">{pct(c.usury.cap)}</span></span>
                <Badge className={`rounded-full text-[10px] hover:bg-inherit ${USURY_STATUS[c.usury.status][1]}`} data-testid={`credit-status-${c.id}`}>{USURY_STATUS[c.usury.status][0]}</Badge>
              </div>}
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground">Aucun crédit renseigné.</p>}
    </div>
  );
};

const FeesForm = ({ onReport }) => {
  const qc = useQueryClient();
  const now = new Date();
  const [v, setV] = useState({ date_from: iso(new Date(now.getFullYear(), now.getMonth() - 3, 1)), date_to: iso(now), fragile: false, overdraft_avg: "", overdraft_days: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/finance/reports/bank-fees", { ...v, overdraft_avg: v.overdraft_avg ? Number(v.overdraft_avg) : null, overdraft_days: v.overdraft_days ? Number(v.overdraft_days) : null });
      qc.invalidateQueries({ queryKey: ["reports"] });
      onReport(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="fees-form">
      <div className="grid gap-2 sm:grid-cols-2">
        <div><Label className="text-xs">Du</Label><DatePicker value={v.date_from} onChange={set("date_from")} required noFuture data-testid="fees-input-from" /></div>
        <div><Label className="text-xs">Au</Label><DatePicker value={v.date_to} onChange={set("date_to")} required noFuture data-testid="fees-input-to" /></div>
        <div><Label className="text-xs">Découvert moyen € (facultatif)</Label><Input type="number" min="1" step="0.01" value={v.overdraft_avg} onChange={set("overdraft_avg")} placeholder="Ex. 400" data-testid="fees-input-overdraft-avg" /></div>
        <div><Label className="text-xs">Jours à découvert (facultatif)</Label><Input type="number" min="1" max="366" value={v.overdraft_days} onChange={set("overdraft_days")} placeholder="Ex. 30" data-testid="fees-input-overdraft-days" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={v.fragile} onCheckedChange={set("fragile")} data-testid="fees-input-fragile" /> Je bénéficie du statut « clientèle fragile » (plafonds réduits)</label>
      <Button type="submit" className="w-full rounded-full sm:w-auto" disabled={busy} data-testid="fees-generate-button">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scale className="mr-2 h-4 w-4" />} Générer le rapport de contrôle
      </Button>
    </form>
  );
};

export const BankFeesPanel = ({ onReport }) => {
  const { data: u } = useQuery({ queryKey: ["usury"], queryFn: () => api.get("/finance/usury").then((r) => r.data) });
  return (
    <section className="card-surface tone-wash tone-butter space-y-5 p-5 sm:p-6" data-testid="bank-fees-panel">
      <div className="flex items-center gap-3">
        <span className="tone-chip flex h-9 w-9 items-center justify-center rounded-xl"><Landmark className="h-4 w-4" /></span>
        <div><h2 className="text-lg font-bold">Frais bancaires & taux d'usure</h2>
          <p className="text-xs text-muted-foreground">Contrôle de vos frais par rapport aux plafonds légaux et de vos crédits et découverts par rapport aux seuils de l'usure de la Banque de France.</p></div>
      </div>
      {u && <p className="rounded-xl bg-card/70 p-3 text-xs" data-testid="usury-period">Seuils de l'usure en vigueur : <b>{u.period}</b> (découvert {pct(u.rates.decouvert)}, crédit &gt; 6 000 € {pct(u.rates.conso_gt6000)}, immobilier fixe ≥ 20 ans {pct(u.rates.immo_fixe_ge20)}).
        {u.expired && <span className="font-semibold text-destructive"> Ces seuils sont expirés : mise à jour nécessaire.</span>} <a href={u.source} target="_blank" rel="noreferrer" className="text-primary underline">Source Banque de France</a></p>}
      <Credits />
      <FeesForm onReport={onReport} />
    </section>
  );
};
